import { zodResolver } from "@hookform/resolvers/zod"
import * as React from "react"
import { useForm } from "react-hook-form"
import { Link, useNavigate } from "react-router-dom"
import { z } from "zod"
import { authApi } from "@/api/auth"
import { ApiError } from "@/api/client"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useAuth } from "@/hooks/use-auth"

const schema = z.object({
  email: z.string().email("Geçerli bir e-posta girin."),
  password: z.string().min(8, "Şifre en az 8 karakter olmalı."),
  orgName: z.string().min(2, "Şirket adı en az 2 karakter."),
})

type FormValues = z.infer<typeof schema>

export function SignupPage() {
  const [error, setError] = React.useState<string | null>(null)
  const [confirmation, setConfirmation] = React.useState<string | null>(null)
  const { refresh } = useAuth()
  const nav = useNavigate()

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "", orgName: "" },
  })

  async function onSubmit(values: FormValues) {
    setError(null)
    setConfirmation(null)
    try {
      const result = await authApi.signUp({
        email: values.email,
        password: values.password,
        org_name: values.orgName,
      })
      if (result.needs_email_confirmation) {
        setConfirmation(result.message ?? "E-postanı kontrol et.")
        return
      }
      await refresh()
      nav("/dashboard", { replace: true })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Kayıt başarısız.")
    }
  }

  if (confirmation) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>E-postanı onayla</CardTitle>
          <CardDescription>{confirmation}</CardDescription>
        </CardHeader>
        <CardContent>
          <Link className="text-sm text-primary hover:underline" to="/login">
            Giriş sayfasına dön
          </Link>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Hesap oluştur</CardTitle>
        <CardDescription>14 gün boyunca ücretsiz dene.</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)} noValidate>
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="space-y-1.5">
            <Label>Şirket adı</Label>
            <Input {...form.register("orgName")} />
            {form.formState.errors.orgName && (
              <p className="text-xs text-destructive">{form.formState.errors.orgName.message}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>E-posta</Label>
            <Input type="email" autoComplete="email" {...form.register("email")} />
            {form.formState.errors.email && (
              <p className="text-xs text-destructive">{form.formState.errors.email.message}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Şifre</Label>
            <Input type="password" autoComplete="new-password" {...form.register("password")} />
            {form.formState.errors.password && (
              <p className="text-xs text-destructive">{form.formState.errors.password.message}</p>
            )}
          </div>
          <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? "Hesap oluşturuluyor…" : "Hesap oluştur"}
          </Button>
          <p className="text-center text-sm text-muted-foreground">
            Zaten hesabın var mı?{" "}
            <Link className="text-primary hover:underline" to="/login">
              Giriş yap
            </Link>
          </p>
        </form>
      </CardContent>
    </Card>
  )
}
